import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import ReactSelect from '../../../../components/ReactSelect';
import { GRID_SELECT } from '../../GscTools/selectStyles';
import {
    OPERATORS,
    addRuleError,
    conditionError,
    emptyRule,
    filterLabel,
    groupsFrom,
    operatorLabel,
    ruleLabel,
} from '../groups';

/**
 * The session's group rules, written in the scope bar beside the dates they will be read over.
 *
 * A rule is a group name, a condition and a value. Rules that share a name are one group and are
 * ANDed, so a second rule under "Guides" narrows Guides rather than starting another group — the
 * one gesture covers both "add a group" and "tighten a group".
 *
 * The filter a rule will be sent as is previewed while it is typed, before anything is added or
 * applied. It is the whole of what Search Console will be told, and the difference between what
 * someone typed and what is matched — an escaped dot, an anchor at the front of a prefix — is
 * visible only in that form.
 *
 * Nothing is saved. The rules live in this page's address once applied and nowhere else; an
 * edit that has not been applied can be thrown away, which puts the applied rules back.
 */
class GroupEditor extends Component {
    constructor(props) {
        super(props);
        this.state = { draft: emptyRule(), error: null };

        this.handleAdd = this.handleAdd.bind(this);
    }

    patch(partial) {
        this.setState((state) => ({ draft: { ...state.draft, ...partial }, error: null }));
    }

    handleAdd(event) {
        event.preventDefault();

        const { t, rules, onChange } = this.props;
        const draft = {
            group: this.state.draft.group.trim(),
            operator: this.state.draft.operator,
            value: this.state.draft.value.trim(),
        };

        // Invalid input keeps the rules in force as they were: nothing is added until the rule
        // is one Search Console can be sent.
        const error = addRuleError(draft, rules, t);
        if (error) return this.setState({ error });

        onChange([...rules, draft]);
        // The name stays, so the next rule typed tightens the same group unless it is renamed.
        this.setState({ draft: { ...emptyRule(), group: draft.group }, error: null });
    }

    /** Whether the rules being edited differ from the ones the report on screen was read under. */
    edited() {
        const { rules, appliedRules } = this.props;
        return !!appliedRules && JSON.stringify(rules) !== JSON.stringify(appliedRules);
    }

    renderPreview() {
        const { t } = this.props;
        const { draft } = this.state;

        // A preview is only shown for a condition that could be sent; a half-typed prefix would
        // otherwise be previewed as a filter it is not going to become.
        const condition = { operator: draft.operator, value: draft.value.trim() };
        const sendable = !conditionError(condition, t);

        return (
            <div className="brand-pattern-preview">
                <h5>{t('tools.content_groups.editor.preview')}</h5>
                {sendable ? (
                    <code className="gsc-tool-expression">{filterLabel(condition)}</code>
                ) : (
                    <span className="gsc-tool-field-note">
                        {t('tools.content_groups.editor.preview_empty')}
                    </span>
                )}
                <p className="gsc-tool-rules-note">
                    {t('tools.content_groups.editor.preview_note')}
                </p>
            </div>
        );
    }

    render() {
        const { t, rules, onChange, onRestore } = this.props;
        const { draft, error } = this.state;

        const operatorOptions = OPERATORS.map((value) => ({
            value,
            label: operatorLabel(t, value),
        }));
        const groups = groupsFrom(rules);

        return (
            <div className="gsc-tool-rules">
                <div className="gsc-tool-rules-head">
                    <h4>{t('tools.content_groups.editor.title')}</h4>
                    <p>{t('tools.content_groups.editor.description')}</p>
                </div>

                <div className="gsc-tool-rules-grid wide-value-grid">
                    <div className="gsc-tool-field">
                        <label htmlFor="cg-rule-group">
                            {t('tools.content_groups.editor.name')}
                        </label>
                        <input
                            id="cg-rule-group"
                            type="text"
                            className="form-control"
                            value={draft.group}
                            list="cg-rule-groups"
                            placeholder={t('tools.content_groups.editor.name_placeholder')}
                            onChange={(event) => this.patch({ group: event.target.value })}
                        />
                        <datalist id="cg-rule-groups">
                            {groups.map((group) => (
                                <option key={group.name} value={group.name} />
                            ))}
                        </datalist>
                    </div>

                    <div className="gsc-tool-field">
                        <label>{t('tools.content_groups.editor.condition')}</label>
                        <ReactSelect
                            options={operatorOptions}
                            value={operatorOptions.find((item) => item.value === draft.operator)}
                            blurInputOnSelect={true}
                            classname="gsc-tool-select"
                            controlStyles={GRID_SELECT}
                            customeOnChange={(item) => this.patch({ operator: item.value })}
                        />
                    </div>

                    <div className="gsc-tool-field">
                        <label htmlFor="cg-rule-value">
                            {t('tools.content_groups.editor.value')}
                        </label>
                        <input
                            id="cg-rule-value"
                            type="text"
                            className="form-control"
                            value={draft.value}
                            placeholder={t(
                                `tools.content_groups.editor.value_placeholder.${draft.operator}`,
                            )}
                            onChange={(event) => this.patch({ value: event.target.value })}
                        />
                    </div>

                    <div className="gsc-tool-rules-add">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleAdd}
                        >
                            {t('tools.content_groups.editor.add')}
                        </button>
                    </div>
                </div>

                <p className="gsc-tool-rules-note">
                    {t('tools.content_groups.editor.matching_note')}
                </p>

                {!!error && <div className="gsc-tool-banner error">{error}</div>}

                {this.renderPreview()}

                {!rules.length ? (
                    <p className="gsc-tool-field-note">{t('tools.content_groups.editor.none')}</p>
                ) : (
                    <div className="gsc-tool-chips">
                        {rules.map((rule, index) => (
                            <span
                                className="gsc-tool-chip"
                                key={`${rule.group}-${rule.operator}-${rule.value}-${index}`}
                            >
                                <span>{ruleLabel(t, rule)}</span>
                                <button
                                    type="button"
                                    aria-label={t('tools.content_groups.editor.remove', {
                                        rule: ruleLabel(t, rule),
                                    })}
                                    onClick={() => onChange(rules.filter((_, at) => at !== index))}
                                >
                                    <i className="icon-x-close" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {this.edited() && (
                    <p className="gsc-tool-rules-note">
                        {t('tools.content_groups.editor.unapplied')}{' '}
                        <button type="button" className="button button-link" onClick={onRestore}>
                            {t('tools.content_groups.editor.restore')}
                        </button>
                    </p>
                )}
            </div>
        );
    }
}

export default withTranslation()(GroupEditor);
